import {
  THEME_COLOR_TOKEN_IDS,
  THEME_META_FIELD_IDS,
} from './themeTokenSchema.js';
import {
  ORBITAL_DEFAULT_PACK,
  ThemeMode,
} from './packs/defaultTheme.js';

/**
 * Normalize a theme mode string against known modes.
 *
 * @param {unknown} value
 * @param {object} [pack]
 * @returns {string|null}
 */
export function normalizeThemeMode(value, pack = ORBITAL_DEFAULT_PACK) {
  if (typeof value !== 'string') {
    return null;
  }

  if (pack?.modes?.[value]) {
    return value;
  }

  const upper = value.trim().toUpperCase();
  return pack?.modes?.[upper] ? upper : null;
}

/**
 * Resolve a theme pack + mode into a flat token map for React (`theme`) and CSS publish.
 *
 * Pack values merge over the builtin default for the same mode so partial packs
 * do not blank the UI.
 *
 * @param {object|null|undefined} pack - Theme pack with modes.DARK / modes.LIGHT.
 * @param {string|null|undefined} mode - ThemeMode.DARK | ThemeMode.LIGHT.
 * @returns {object} Resolved theme token map (includes meta fields).
 */
export function resolveTheme(pack, mode) {
  const resolvedMode = normalizeThemeMode(mode, ORBITAL_DEFAULT_PACK)
    || ThemeMode.DARK;

  const defaults = ORBITAL_DEFAULT_PACK.modes[resolvedMode]
    || ORBITAL_DEFAULT_PACK.modes[ThemeMode.DARK];

  const packModes = pack?.modes && typeof pack.modes === 'object'
    ? pack.modes
    : null;

  const packModeValues = packModes?.[resolvedMode]
    || packModes?.[ThemeMode.DARK]
    || null;

  const merged = {
    ...defaults,
    ...(packModeValues && typeof packModeValues === 'object' ? packModeValues : {}),
    key: resolvedMode,
  };

  // Ensure every catalog color token has a value from defaults when missing/empty.
  THEME_COLOR_TOKEN_IDS.forEach((tokenId) => {
    const value = merged[tokenId];
    if (value === undefined || value === null || value === '') {
      merged[tokenId] = defaults[tokenId];
    }
  });

  THEME_META_FIELD_IDS.forEach((fieldId) => {
    if (fieldId === 'key') {
      return;
    }
    if (merged[fieldId] === undefined) {
      merged[fieldId] = defaults[fieldId];
    }
  });

  return merged;
}

/**
 * Build a ThemeMode → resolved theme map for the given pack (API compat with old THEME_MAP).
 *
 * @param {object} [pack]
 * @returns {Record<string, object>}
 */
export function buildThemeMapForPack(pack = ORBITAL_DEFAULT_PACK) {
  return {
    [ThemeMode.DARK]: resolveTheme(pack, ThemeMode.DARK),
    [ThemeMode.LIGHT]: resolveTheme(pack, ThemeMode.LIGHT),
  };
}
